import React, { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Single or multi image uploader with drag-and-drop */
export function ImageUploader({ value = [], onChange, multiple = true, maxFiles = 8, testId = "image-uploader" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const results = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload?kind=image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results.push(`${BACKEND}${data.url}`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    const next = multiple ? [...value, ...results].slice(0, maxFiles) : results.slice(0, 1);
    onChange(next);
    setBusy(false);
    if (results.length) toast.success(`${results.length} image${results.length > 1 ? "s" : ""} uploaded`);
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); upload(Array.from(e.dataTransfer.files)); };

  return (
    <div data-testid={testId}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${dragging ? "border-[#0891B2] bg-[#0891B2]/5" : "border-slate-200 hover:border-white/20"}`}
      >
        <input ref={inputRef} type="file" accept="image/*" multiple={multiple} className="hidden"
          onChange={(e) => upload(Array.from(e.target.files || []))} />
        {busy ? <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#0891B2]" /> :
          <UploadCloud className="w-6 h-6 mx-auto text-[#475569]" />}
        <div className="mt-2 text-sm text-[#475569]">
          <span className="text-[#0891B2] font-medium">Click to upload</span> or drag & drop
        </div>
        <div className="text-xs text-[#64748B] mt-1">PNG, JPG, WebP · Up to {maxFiles} images</div>
      </div>
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
          {value.map((url, i) => (
            <div key={url + i} className="relative aspect-square rounded overflow-hidden border border-slate-200 group">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="absolute top-1 right-1 bg-black/70 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Uploader for STL / 3D / PDF files (returns list of {id, url, filename}) */
export function FileUploader({ value = [], onChange, accept = ".stl,.obj,.step,.stp,.pdf", testId = "file-uploader" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const results = [...value];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload?kind=file", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results.push({ id: data.id, url: `${BACKEND}${data.url}`, filename: data.filename, size: data.size });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    onChange(results);
    setBusy(false);
  };

  return (
    <div data-testid={testId}>
      <button type="button" onClick={() => inputRef.current?.click()} className="tl-btn-ghost text-sm w-full justify-center">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><UploadCloud className="w-4 h-4" /> Upload 3D File</>}
      </button>
      <input ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={(e) => upload(Array.from(e.target.files || []))} />
      {value.length > 0 && (
        <div className="mt-3 space-y-2">
          {value.map((f, i) => (
            <div key={f.id || i} className="flex items-center gap-2 p-2 rounded bg-[#FFFFFF] border border-slate-200 text-sm">
              <FileText className="w-4 h-4 text-[#0891B2]" />
              <span className="flex-1 truncate">{f.filename}</span>
              <span className="text-xs text-[#64748B]">{Math.round((f.size || 0) / 1024)} KB</span>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}><X className="w-3 h-3 text-[#475569]" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
